<template>
  <div class="main-wrapper">
    <layouts />
    <sidebar />

    <div class="page-wrapper">
      <div class="content">
        <pageaddheader
          title="Purchase Audit"
          title1="View what changed in purchases"
          :items="null"
        />

        <div class="card">
          <div class="card-body">
            <div class="table-top">
              <div class="row w-100">
                <!-- Start Date -->
                <div class="col-lg-2 col-sm-6 col-12">
                  <div class="form-group">
                    <div class="input-groupicon">
                      <datepicker
                        v-model="startDate"
                        placeholder="Start Date"
                        class="picker"
                        :editable="true"
                        :clearable="false"
                      />
                      <div class="addonset">
                        <img src="../../../assets/img/icons/calendars.svg" />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- End Date -->
                <div class="col-lg-2 col-sm-6 col-12">
                  <div class="form-group">
                    <div class="input-groupicon">
                      <datepicker
                        v-model="endDate"
                        placeholder="End Date"
                        class="picker"
                        :editable="true"
                        :clearable="false"
                      />
                      <div class="addonset">
                        <img src="../../../assets/img/icons/calendars.svg" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Table -->
            <div class="table-responsive">
              <a-table
                :columns="columns"
                :data-source="filteredAudits"
                :rowKey="record => record.audit_id"
                :expandRowByClick="true"
              >
                <!-- Expanded Row -->
                <template #expandedRowRender="{ record }">
                  <table class="table table-sm table-bordered audit-diff-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Old Value</th>
                        <th>New Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="diff in getDiff(record.old_data, record.new_data)"
                        :key="diff.field"
                      >
                        <td>{{ diff.field }}</td>
                        <td class="text-danger">{{ diff.old }}</td>
                        <td class="text-success">{{ diff.new }}</td>
                      </tr>

                      <tr v-if="getDiff(record.old_data, record.new_data).length === 0">
                        <td colspan="3" class="text-center text-muted">
                          No field-level changes
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </template>

                <!-- Cell formatting -->
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'changed_at'">
                    {{ formatDate(record.changed_at) }}
                  </template>

                  <template v-if="column.key === 'operation'">
                    <span class="badge" :class="operationClass(record.operation)">
                      {{ record.operation }}
                    </span>
                  </template>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import axios from "axios";
import { mapGetters } from "vuex";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const columns = [
  {
    title: "Item Name",
    dataIndex: "item_name",
    key: "item_name",
  },
  {
    title: "Operation",
    dataIndex: "operation",
    key: "operation",
  },
  {
    title: "Changed At",
    dataIndex: "changed_at",
    key: "changed_at",
    sorter: (a, b) => new Date(a.changed_at) - new Date(b.changed_at),
  },
];

export default {
  name: "PurchaseAudit",

  data() {
    return {
      data: [],
      search: "",
      columns,
      startDate: null,
      endDate: null,
      isLoading: false,
    };
  },

  computed: {
    ...mapGetters(["getCurrentLocation"]),

    locationId() {
      return this.getCurrentLocation?.id || null;
    },

    filteredAudits() {
      if (!this.search) return this.data;
      return this.data.filter(row =>
        JSON.stringify(row).toLowerCase().includes(this.search.toLowerCase())
      );
    },
  },

  watch: {
    // ✅ SINGLE watcher → prevents double API calls
    startDate: "onDateChange",
    endDate: "onDateChange",
  },

  methods: {
    onDateChange() {
      if (this.startDate && this.endDate) {
        this.getAuditLogs();
      }
    },

    setDefaultDates() {
      const now = new Date();
      this.startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      this.endDate = new Date();
    },

    async getAuditLogs() {
      if (this.isLoading) return;

      try {
        this.isLoading = true;

        const params = {
          locationid: this.locationId,
          start_date: this.startDate.toLocaleDateString("en-CA"),
          end_date: this.endDate.toLocaleDateString("en-CA"),
        };

        const res = await axios.get("/v1/audit/purchase", { params });

        // ✅ Normalize API response (BEST PRACTICE)
        this.data = res.data.map(r => ({
          ...r.PurchaseAudit,
          item_name: r.item_name,
        }));
      } catch (err) {
        console.error("Audit load failed", err);
      } finally {
        this.isLoading = false;
      }
    },

    getDiff(oldData = {}, newData = {}) {
      const diffs = [];
      for (const key in newData) {
        if (oldData?.[key] !== newData?.[key]) {
          diffs.push({
            field: key,
            old: oldData?.[key] ?? "-",
            new: newData?.[key] ?? "-",
          });
        }
      }
      return diffs;
    },

    formatDate(date) {
      return date ? new Date(date).toLocaleString() : "";
    },

    operationClass(op) {
      return {
        "bg-success": op === "INSERT",
        "bg-warning": op === "UPDATE",
        "bg-danger": op === "DELETE",
      };
    },
  },

  mounted() {
    this.setDefaultDates(); // API will be called ONCE via watcher
  },
};
</script>


<style>
.excel-icon {
  width: 25px;
  height: 25px;
}

.audit-diff-table {
  background: #f9fafb;
  font-size: 13px;
}

.badge {
  padding: 5px 10px;
  font-size: 12px;
  border-radius: 4px;
}

.bg-success {
  background: #28a745;
  color: white;
}

.bg-warning {
  background: #ffc107;
  color: black;
}

.bg-danger {
  background: #dc3545;
  color: white;
}

.ant-tooltip {
  display: none !important;
}
</style>
